/**
 * PanelList — the ONE list body for WorkspacePanel content (TD-004).
 * Owns the flat divided rows (DD-002), the page slice, and the
 * bottom-right pager (DD-015). Panels pass items + renderItem and never
 * wire pagination themselves.
 */

import { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { PanelPagination, usePagedList } from "@/components/ui/PanelPagination";

interface PanelListProps<T> {
  items: T[];
  /** Rows per page. The fixed home-panel body height (DD-002) is sized so
   * a full default page (uniform min-h-24 rows + pager) fits without
   * internal scroll — change them together. */
  pageSize?: number;
  renderItem: (item: T, index: number) => ReactNode;
  keyFor?: (item: T, index: number) => string | number;
  /** Rendered instead of the list when there are no items. */
  empty?: ReactNode;
  /** Hairline dividers between rows (default). Rows that carry their own
   * tint as separator (calendar strips) turn this off. */
  divided?: boolean;
  /** Classes on each row wrapper. The default gives every row the same
   * minimum height with centered content so lists built from different
   * row components share one rhythm (DD-002) — generous enough that a
   * clamped review row almost fits it. */
  rowClassName?: string;
  className?: string;
}

export function PanelList<T>({
  items,
  pageSize = 5,
  renderItem,
  keyFor,
  empty = null,
  divided = true,
  rowClassName = "min-h-24 flex flex-col justify-center",
  className,
}: PanelListProps<T>) {
  const pager = usePagedList(items, pageSize);

  if (items.length === 0) return <>{empty}</>;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className={divided ? "divide-y divide-gray-100" : "flex flex-col gap-1"}>
        {pager.paged.map((item, i) => (
          <div key={keyFor ? keyFor(item, i) : pager.start + i} className={rowClassName}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
      <PanelPagination
        page={pager.page}
        pageCount={pager.pageCount}
        total={pager.total}
        start={pager.start}
        end={pager.end}
        onPrev={pager.prev}
        onNext={pager.next}
      />
    </div>
  );
}
